import React, { useEffect, useState } from "react";
import "./OrdersTable.css";
import {
  BorderColorIcon,
  FilterAltOutlinedIcon,
} from "../../components/icons/Icons";
import OrdersModal from "./OrdersModal";
import OrderDetailsModal from "./OrderDetailsModal";
import axiosInstance from "../../axiosInstance";
import { formatDate } from "../../utils/Formatdate";

interface Order {
  id: string;
  createdAt: string;
  amount: number;
  status: string;
  customer: {
    full_name: string;
    phone_number: string;
  };
  order_items: {
    id: string;
    name: string;
    description: string;
    quantity: number;
    price: number;
    line_total: number;
  }[];
}

const OrdersTable: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [orderDetail, setOrderDetail] = useState<Order | null>(null);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch orders from API (server-side pagination)
  const getOrders = async (page = currentPage, limit = entriesPerPage) => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `/orders/venture?page=${page}&limit=${limit}`
      );

      const data = res.data.data;

      setOrders(data.orders || []);
      setFilteredOrders(data.orders || []);
      setTotalOrders(data.pagination?.total || 0);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      setOrders([]);
      setFilteredOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  // Refetch when page or entriesPerPage changes
  useEffect(() => {
    getOrders(currentPage, entriesPerPage);
  }, [currentPage, entriesPerPage]);

  // Local filter handler (client-side filtering on current page only)
  const handleFilter = (filters: any) => {
    let filtered = orders;

    if (filters.customerName) {
      filtered = filtered.filter((order) =>
        order.customer?.full_name
          .toLowerCase()
          .includes(filters.customerName.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (order) => order.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.orderId) {
      filtered = filtered.filter((order) =>
        order.id.toLowerCase().includes(filters.orderId.toLowerCase())
      );
    }

    if (filters.date) {
      filtered = filtered.filter((order) =>
        formatDate(order.createdAt).includes(filters.date)
      );
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalOrders / entriesPerPage);

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleEntriesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEntriesPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleOrderModal = (order: Order) => {
    setEditModalOpen(true);
    setOrderDetail(order);
  };

  const currentOrders = filteredOrders;

  return (
    <div className="orders-table">
      <div className="orders__table__header">
        <h3>All Orders</h3>
        <button onClick={() => setFilterModalOpen(true)}>
          <FilterAltOutlinedIcon />
          <p>Filter</p>
        </button>
      </div>

      <div className="table-s">
        <table>
          <thead>
            <tr>
              <th>ORDER DATE</th>
              <th>ORDER ID</th>
              <th>CUSTOMER NAME</th>
              <th>AMOUNT</th>
              <th>STATUS</th>
              <th>DETAILS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  Loading orders...
                </td>
              </tr>
            ) : currentOrders.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No orders found
                </td>
              </tr>
            ) : (
              currentOrders.map((order) => (
                <tr key={order.id}>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>{order.id}</td>
                  <td>{order.customer?.full_name}</td>
                  <td>${order.amount}</td>
                  <td>{order.status}</td>
                  <td>
                    <button
                      className="edit-button"
                      onClick={() => handleOrderModal(order)}
                    >
                      <BorderColorIcon />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Entries dropdown and pagination info */}
      <div className="entries-control">
        <label>
          View
          <select
            className="entries-select"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            <option value="2">2</option>
            <option value="5">5</option>
            <option value="10">10</option>
          </select>
          Per Page
        </label>

        <div className="right__pagination">
          <p>
            Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
            {Math.min(currentPage * entriesPerPage, totalOrders)} of {totalOrders} entries
          </p>

          <div className="pagination">
            <button
              className="pagination__button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`pagination__button ${
                  currentPage === page ? "active" : ""
                }`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="pagination__button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <OrdersModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onFilter={handleFilter}
      />

      <OrderDetailsModal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        orderDetails={orderDetail}
        onStatusUpdated={() => getOrders(currentPage, entriesPerPage)}
      />
    </div>
  );
};

export default OrdersTable;